import { useLayoutEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronRight, ChevronUp } from 'lucide-react'
import { nativeSportsbookApi } from '@/services/api'
import { americanOdds } from './nativePreviewModel'
import { boardQuery, boardQueryComplete, lineText, sampleBoard, selectionLabel } from './playerPropBoardModel'
import { revealWithin } from './nativePreviewScroll'
import styles from './NativePreview.module.css'
import { isLiveSource } from './catalogSources'

// Under live data the board comes from the tenant runtime the app reads; sample
// games answer only the canonical sample requests, never a borrowed board.
function usePlayerPropBoard(component, catalog, slug) {
  const query = boardQuery(component)
  const complete = boardQueryComplete(query)
  const live = isLiveSource(catalog?.source)
  const remote = useQuery({
    queryKey: ['native-player-prop-board', slug, catalog?.source, query],
    enabled: live && complete && Boolean(slug) && !catalog?.rehearsal,
    queryFn: ({ signal }) => nativeSportsbookApi.previewPlayerPropBoard(slug, query, signal, catalog.source),
    staleTime: 5000, refetchInterval: 15000, retry: false,
  })
  if (!complete) return { state: 'incomplete' }
  if (catalog?.rehearsal === 'empty') return { state: 'empty' }
  if (catalog?.rehearsal === 'unavailable_inventory' || catalog?.rehearsal === 'unavailable_markets') return { state: 'unavailable' }
  if (!live) {
    const board = sampleBoard(query)
    return board ? { state: board.state, board } : { state: 'sample_missing' }
  }
  if (remote.isError) return { state: 'unavailable' }
  if (!remote.data) return { state: remote.isFetching ? 'loading' : 'unavailable' }
  return { state: remote.data.state, board: remote.data }
}

function PropCell({ row, column, authored, cell }) {
  if (!cell) return <span className={styles.propEmptyCell} aria-label="Not offered">—</span>
  const suspended = cell.status === 'SUSPENDED' || cell.market_status === 'SUSPENDED'
  const price = cell.odds_american || americanOdds(cell.odds_millis)
  const caption = (column.selection === 'over' || column.selection === 'under') && cell.line_millis != null ? lineText(cell.line_millis) : null
  return (
    <span className={`${styles.odds} ${styles.propCell}`} data-open={suspended ? 'false' : 'true'} role="button" tabIndex={suspended ? -1 : 0} aria-label={`${selectionLabel(row, column, authored.label, cell)} ${price}`}>
      {caption ? <span className={styles.oddsCaption}>{caption}</span> : null}
      {suspended ? <span className={styles.susp}>SUSP</span> : <span className={styles.oddsValue}>{price}</span>}
    </span>
  )
}

export function PlayerPropBoardPreview({ component, catalog, slug, interactive }) {
  const { state, board } = usePlayerPropBoard(component, catalog, slug)
  const [expanded, setExpanded] = useState(false)
  const root = useRef(null)
  const revealAfterCollapse = useRef(false)
  // Collapsing from the footer shrinks the board under the reader, so bring its header back,
  // scrolling only the phone's Home view and never the Studio page around it.
  useLayoutEffect(() => {
    if (expanded || !revealAfterCollapse.current) return
    revealAfterCollapse.current = false
    revealWithin(root.current?.closest('[data-scroller="y"]'), root.current, 'y', 'nearest')
  }, [expanded])
  const columns = component.columns || []
  if (state === 'empty' && component.empty_behavior === 'hide') {
    return interactive ? <div className={styles.placeholder}>{component.title}: hidden while the provider posts none of these props.</div> : null
  }
  const rows = state === 'ready' ? board.rows : []
  const expandable = rows.length > (component.player_limit || 5)
  const visible = expanded ? rows : rows.slice(0, component.player_limit || 5)
  const message = {
    incomplete: 'Choose the game or team and at least one market.',
    loading: 'Loading player props…',
    unavailable: 'Player props are unavailable. Try again shortly.',
    empty: 'No player props posted for this board yet.',
    sample_missing: 'Sample data includes only the provider templates. Rehearse with live data to preview this board.',
  }[state]
  return (
    <div ref={root} className={`${styles.card} ${styles.propBoard}`} data-testid="player-prop-board">
      <div className={styles.propBoardHeader}>
        <span className={styles.propBoardTitle}>{component.title}</span>
        {/* While editing, a click selects the section, so See all is a label like other preview actions. */}
        {expandable
          ? interactive
            ? <span className={styles.propSeeAll} aria-hidden="true">SEE ALL<ChevronRight size={14} aria-hidden="true" /></span>
            : <button type="button" className={styles.propSeeAll} onClick={() => setExpanded(!expanded)}>
              {expanded ? 'SHOW LESS' : 'SEE ALL'}{expanded ? <ChevronUp size={14} aria-hidden="true" /> : <ChevronRight size={14} aria-hidden="true" />}
            </button>
          : null}
      </div>
      {board?.source === 'fixture' ? <div className={styles.fixtureNotice}>Fixture markets · Not live inventory</div> : null}
      {state === 'ready' ? (<>
        <div className={styles.propGrid} style={{ '--prop-columns': columns.length }}>
          <div className={styles.propBadges}>
            <span className={styles.propBadge}>{board.competition.name.toUpperCase()}</span>
            {board.same_game_parlay ? <span className={`${styles.propBadge} ${styles.propBadgeSgp}`}>SGP</span> : null}
          </div>
          {columns.map((column) => <span key={column.id} className={styles.propHeaderLabel}>{column.label}</span>)}
          {visible.map((row) => [
            <div key={`${row.event_id}:${row.player.id}`} className={styles.propPlayer}>
              <span className={styles.propPlayerName}>{row.player.name}</span>
              {row.team ? <span className={styles.propPlayerTeam}>{row.team.name}</span> : null}
            </div>,
            ...row.cells.map((cell, index) => <PropCell key={`${row.event_id}:${row.player.id}:${columns[index]?.id}`} row={row} column={board.columns[index]} authored={columns[index]} cell={cell} />),
          ])}
        </div>
        {/* A long expanded list also collapses from its end, as in the apps. */}
        {expanded && expandable && !interactive
          ? <button type="button" className={`${styles.propSeeAll} ${styles.propShowLessFooter}`} aria-label="Show fewer players" onClick={() => { revealAfterCollapse.current = true; setExpanded(false) }}>
            SHOW LESS<ChevronUp size={14} aria-hidden="true" />
          </button>
          : null}
      </>) : (
        <div className={state === 'unavailable' ? `${styles.placeholder} ${styles.propMessage}` : styles.placeholder} aria-busy={state === 'loading' || undefined}>
          <div>{message}</div>
          {state === 'unavailable' ? <span className={styles.betwireAction} aria-hidden="true">Retry</span> : null}
        </div>
      )}
    </div>
  )
}
